<template>
  <div class="profile-dashboard">
    <!-- Page Header -->
    <div class="page-header">
      <h1>My Profile</h1>
    </div>

    <!-- Forms Grid -->
    <div class="forms-grid">
      <!-- Account Info Card -->
      <section class="card">
        <header class="card-header">Account Information</header>
        <div class="card-body">
          <form @submit.prevent="saveProfile" class="form-grid">
            <div class="form-group">
              <label for="firstName">First Name</label>
              <input id="firstName" v-model="form.first_name" type="text" required />
            </div>
            <div class="form-group">
              <label for="lastName">Last Name</label>
              <input id="lastName" v-model="form.last_name" type="text" required />
            </div>
            <div class="form-group">
              <label for="email">Email</label>
              <input id="email" v-model="form.email" type="email" required />
            </div>
            <div class="form-group">
              <label for="avatar">Avatar URL</label>
              <input id="avatar" v-model="form.avatar" type="url" placeholder="https://..." />
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-save" :disabled="saving">
                {{ saving ? 'Saving…' : 'Save Profile' }}
              </button>
              <button type="button" class="btn-cancel" @click="resetForm" :disabled="saving">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </section>

      <!-- Change Password Card -->
      <section class="card">
        <header class="card-header">Change Password</header>
        <div class="card-body">
          <form @submit.prevent="changePassword" class="form-grid">
            <div class="form-group password-group">
              <label for="currentPassword">Current Password</label>
              <input
                :type="show.current ? 'text' : 'password'"
                id="currentPassword"
                v-model="pw.current"
                required
              />
              <button
                type="button"
                class="toggle-btn"
                @click="show.current = !show.current"
                aria-label="Toggle visibility"
              >
                <svg v-if="!show.current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.269-2.944-9.543-7a10.05 10.05 0 011.655-3.221M6.475 6.475A9.953 9.953 0 0112 5c4.478 0 8.269 2.944 9.543 7a9.953 9.953 0 01-1.56 3.379" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 3l18 18" />
                </svg>
              </button>
            </div>

            <div class="form-group password-group">
              <label for="newPassword">New Password</label>
              <input
                :type="show.new ? 'text' : 'password'"
                id="newPassword"
                v-model="pw.new"
                minlength="8"
                required
              />
              <button type="button" class="toggle-btn" @click="show.new = !show.new" aria-label="Toggle visibility">
                <svg v-if="!show.new" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.269-2.944-9.543-7a10.05 10.05 0 011.655-3.221M6.475 6.475A9.953 9.953 0 0112 5c4.478 0 8.269 2.944 9.543 7a9.953 9.953 0 01-1.56 3.379" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 3l18 18" />
                </svg>
              </button>
            </div>

            <div class="form-group password-group">
              <label for="confirmPassword">Confirm New Password</label>
              <input
                :type="show.confirm ? 'text' : 'password'"
                id="confirmPassword"
                v-model="pw.confirm"
                minlength="8"
                required
              />
              <button type="button" class="toggle-btn" @click="show.confirm = !show.confirm" aria-label="Toggle visibility">
                <svg v-if="!show.confirm" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.269-2.944-9.543-7a10.05 10.05 0 011.655-3.221M6.475 6.475A9.953 9.953 0 0112 5c4.478 0 8.269 2.944 9.543 7a9.953 9.953 0 01-1.56 3.379" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 3l18 18" />
                </svg>
              </button>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-save" :disabled="changing">
                {{ changing ? 'Updating…' : 'Update Password' }}
              </button>
            </div>
          </form>
        </div>
      </section>

      <!-- Go Back Button -->
      <button class="btn-back-final" @click="router.back()">Go Back</button>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import axios from 'axios'

const store = useStore()
const router = useRouter()

// Account form
const user = store.getters.getUser
const form = reactive({
  first_name: user.first_name || '',
  last_name:  user.last_name  || '',
  email:      user.email      || '',
  avatar:     user.avatar     || ''
})

// Password form
const pw = reactive({ current: '', new: '', confirm: '' })

// Toggle visibility
const show = reactive({ current: false, new: false, confirm: false })

// Loading
const saving   = ref(false)
const changing = ref(false)

function resetForm() {
  form.first_name = user.first_name
  form.last_name  = user.last_name
  form.email      = user.email
  form.avatar     = user.avatar
}

async function saveProfile() {
  saving.value = true
  try {
    await store.dispatch('user/updateProfile', { ...form })
    router.go(0)
  } catch {
    alert('Error saving profile.')
  } finally {
    saving.value = false
  }
}

async function changePassword() {
  if (pw.new !== pw.confirm) return alert('Passwords do not match.')
  changing.value = true
  try {
    await axios.post('/api/auth/change-password', {
      current_password: pw.current,
      new_password: pw.new
    })
    alert('Password updated.')
    pw.current = pw.new = pw.confirm = ''
  } catch {
    alert('Error updating password.')
  } finally {
    changing.value = false
  }
}
</script>

<style lang="scss" scoped>
$primary:   #133831;
$light:     #F3F6F5;
$dark:      #212529;

.page-header {
  background: $primary;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  h1 {
    margin: 0;
    color: white;
    font-size: 1.5rem;
  }
}

.forms-grid {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card {
  width: 100%;
  max-width: 720px;
  background: white;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 6px rgba($dark, 0.1);

  &-header {
    background: $primary;
    color: white;
    padding: 1rem 1.5rem;
    font-weight: 500;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
  }

  &-body {
    padding: 1.5rem;
  }
}

.form-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.form-group {
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
    color: $dark;
    font-size: 0.9rem;
  }

  input {
    padding: 0.5rem;
    border: 1px solid $light;
    border-radius: 0.5rem;
    font-size: 0.9rem;
  }
}

.password-group {
  position: relative;

  .toggle-btn {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0;
    color: $dark;
    cursor: pointer;
    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  .btn-save,
  .btn-cancel {
    flex: 1;
    padding: 0.75rem;
    font-weight: 500;
    border-radius: 0.5rem;
    cursor: pointer;
  }

  .btn-save {
    background: $primary;
    color: white;
    border: none;
    &:disabled {
      opacity: 0.6;
    }
  }

  .btn-cancel {
    background: white;
    color: $primary;
    border: 2px solid $primary;
    &:disabled {
      opacity: 0.6;
    }
  }
}

/* Final Go Back */
.btn-back-final {
  background: none;
  border: none;
  color: $primary;
  text-decoration: underline;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1rem;
}
</style>
